class DestroyableBase {
  listeners = [];
  __destroyed = false;
  get destroyed() {
    return this.__destroyed;
  }
  addEventListener(type, listener) {
    if (type === "destroy") {
      if (this.__destroyed) {
        this.emitDestroyableEvent(new Event("destroy"), listener);
      } else {
        this.listeners.push(listener);
      }
    }
  }
  removeEventListener(type, listener) {
    if (type === "destroy") {
      const pos = this.listeners.indexOf(listener);
      if (pos >= 0) {
        this.listeners.splice(pos, 1);
      }
    }
  }
  /**
   * Override and call super.destroy() after clean-up.
   */
  destroy() {
    this.__destroyed = true;
    this.emitDestroyableEvent(new Event("destroy"));
    this.listeners.length = 0;
  }
  /**
   * Behaves like EventTarget.dispatchEvent().
   */
  emitDestroyableEvent(event, listener) {
    const listeners = listener !== void 0 ? [listener] : this.listeners;
    let returnValue = true;
    for (let listener2 of listeners) {
      if ("handleEvent" in listener2) {
        listener2.handleEvent(event);
      } else {
        listener2(event);
      }
      if (event.cancelable && event.defaultPrevented) {
        returnValue = false;
      }
    }
    return returnValue;
  }
}
class DestroyableSet extends DestroyableBase {
  destroyables = /* @__PURE__ */ new Set();
  constructor(parent) {
    super();
    if (parent !== void 0) {
      parent.addDestroyable(this);
    }
  }
  addDestroyable(destroyable) {
    if ("destroyed" in destroyable) {
      if (destroyable.destroyed) {
        return;
      }
      destroyable.addEventListener("destroy", () => this.destroyables.delete(destroyable));
    }
    this.destroyables.add(destroyable);
  }
  addDestroyables(destroyables) {
    destroyables.forEach((destroyable) => this.addDestroyable(destroyable));
  }
  removeDestroyable(destroyable) {
    this.destroyables.delete(destroyable);
  }
  destroy() {
    if (!this.destroyed) {
      this.destroyables.forEach((destroyable) => destroyable.destroy());
      this.destroyables.clear();
      super.destroy();
    }
  }
}
class DestroyableTimeout extends DestroyableBase {
  ref;
  constructor(callback, timeout) {
    super();
    this.ref = setTimeout(() => {
      this.destroy();
      callback();
    }, timeout);
  }
  destroy() {
    if (this.ref !== void 0) {
      clearTimeout(this.ref);
      this.ref = void 0;
      super.destroy();
    }
  }
}
class DestroyableInterval extends DestroyableBase {
  ref;
  constructor(callback, interval) {
    super();
    this.ref = setInterval(callback, interval);
  }
  destroy() {
    if (this.ref !== void 0) {
      clearInterval(this.ref);
      this.ref = void 0;
      super.destroy();
    }
  }
}
class DestroyableEventListener extends DestroyableBase {
  remove;
  constructor(target, type, listener, options) {
    super();
    target.addEventListener(type, listener, options);
    this.remove = () => target.removeEventListener(type, listener, options);
  }
  destroy() {
    if (this.remove !== void 0) {
      this.remove();
      super.destroy();
    }
  }
}
class DestroyableAbortController extends DestroyableBase {
  abortController = new AbortController();
  get signal() {
    return this.abortController.signal;
  }
  abort() {
    this.destroy();
  }
  destroy() {
    if (!this.destroyed) {
      this.abortController.abort();
      super.destroy();
    }
  }
}
const destroyableTimeout = (callback, timeout) => new DestroyableTimeout(callback, timeout);
const destroyableInterval = (callback, interval) => new DestroyableInterval(callback, interval);
const destroyableEventListener = (target, type, listener, options) => new DestroyableEventListener(target, type, listener, options);
const whenDestroyed = (destroyable) => new Promise((resolve) => destroyable.addEventListener("destroy", () => resolve()));
export {
  DestroyableAbortController,
  DestroyableBase,
  DestroyableEventListener,
  DestroyableInterval,
  DestroyableSet,
  DestroyableTimeout,
  destroyableEventListener,
  destroyableInterval,
  destroyableTimeout,
  whenDestroyed
};
